'use client';

import { useState, useEffect, useCallback } from 'react';
import { MILESTONE_LABELS, type MilestoneType, type SuccessStoryStatus } from '@/lib/types/success-tracking';

const CEO_PIN = '5050';

interface Candidate {
  id: string;
  user_id: string;
  user_type: 'worker' | 'contractor';
  user_name: string | null;
  user_email: string | null;
  score: number;
  milestone_count: number;
  latest_milestone: string | null;
  nps_score: number | null;
  status: SuccessStoryStatus;
  admin_notes: string | null;
  created_at: string;
  updated_at: string;
}

interface Milestone {
  id: string;
  user_id: string;
  user_type: string;
  milestone_type: string;
  metadata: Record<string, unknown>;
  created_at: string;
}

interface SurveyResponse {
  id: string;
  user_id: string;
  user_type: string;
  survey_type: string;
  responses: Record<string, string>;
  nps_score: number | null;
  created_at: string;
}

const STATUS_COLORS: Record<SuccessStoryStatus, string> = {
  candidate: 'bg-blue-500/20 text-blue-300',
  contacted: 'bg-yellow-500/20 text-yellow-300',
  agreed: 'bg-green-500/20 text-green-300',
  published: 'bg-purple-500/20 text-purple-300',
  declined: 'bg-gray-500/20 text-gray-400',
};

const STATUS_OPTIONS: SuccessStoryStatus[] = ['candidate', 'contacted', 'agreed', 'published', 'declined'];

function timeAgo(iso: string): string {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

export default function SuccessTrackingPage() {
  const [tab, setTab] = useState<'candidates' | 'milestones' | 'surveys'>('candidates');
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [surveys, setSurveys] = useState<SurveyResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCandidate, setSelectedCandidate] = useState<string | null>(null);
  const [editingNotes, setEditingNotes] = useState<string>('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { 'x-ceo-pin': CEO_PIN };

      if (tab === 'candidates') {
        const res = await fetch(`/api/success-tracking/candidates?pin=${CEO_PIN}`);
        const data = await res.json();
        setCandidates(data.data || []);
      } else if (tab === 'milestones') {
        const res = await fetch('/api/success-tracking/milestones', { headers });
        const data = await res.json();
        setMilestones(data.data || []);
      } else {
        // Surveys — fetch via admin endpoint
        const res = await fetch(`/api/success-tracking/surveys?pin=${CEO_PIN}`);
        if (res.ok) {
          const data = await res.json();
          setSurveys(data.data || []);
        }
      }
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch');
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateCandidateStatus = async (userId: string, status: SuccessStoryStatus) => {
    try {
      await fetch('/api/success-tracking/candidates', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'x-ceo-pin': CEO_PIN },
        body: JSON.stringify({ user_id: userId, status }),
      });
      fetchData();
    } catch {}
  };

  const updateCandidateNotes = async (userId: string) => {
    try {
      await fetch('/api/success-tracking/candidates', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'x-ceo-pin': CEO_PIN },
        body: JSON.stringify({ user_id: userId, admin_notes: editingNotes }),
      });
      setSelectedCandidate(null);
      fetchData();
    } catch {}
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-gray-950/95 backdrop-blur border-b border-white/10 px-4 py-3">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold">🏆 Success Tracking</h1>
            <p className="text-xs text-gray-500">Identify success stories from day 1</p>
          </div>
          <button onClick={fetchData} className="p-2 rounded-lg bg-white/5 active:bg-white/10">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mt-3">
          {(['candidates', 'milestones', 'surveys'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`flex-1 py-2 text-xs font-medium rounded-lg transition ${
                tab === t ? 'bg-white/10 text-white' : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              {t === 'candidates' ? '🏆 Candidates' : t === 'milestones' ? '🎯 Milestones' : '📋 Surveys'}
            </button>
          ))}
        </div>
      </div>

      <div className="px-4 py-4 max-w-lg mx-auto space-y-3 pb-20">
        {loading && (
          <div className="flex justify-center py-8">
            <div className="animate-spin w-6 h-6 border-2 border-white/20 border-t-white rounded-full" />
          </div>
        )}

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 text-center text-sm text-red-300">
            {error}
          </div>
        )}

        {/* Candidates Tab */}
        {!loading && tab === 'candidates' && (
          <>
            {candidates.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <p className="text-4xl mb-3">🌱</p>
                <p className="text-sm">No success candidates yet</p>
                <p className="text-xs mt-1">They&apos;ll appear as users hit milestones</p>
              </div>
            ) : (
              candidates.map((c) => (
                <div key={c.id} className="bg-white/5 border border-white/10 rounded-xl p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-sm">{c.user_type === 'contractor' ? '🏗️' : '👷'}</span>
                      <span className="font-medium text-sm">{c.user_name || 'Unknown'}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${STATUS_COLORS[c.status]}`}>
                        {c.status}
                      </span>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-yellow-400">{c.score}</div>
                      <div className="text-xs text-gray-500">points</div>
                    </div>
                  </div>

                  <div className="flex gap-3 text-xs text-gray-400">
                    <span>🎯 {c.milestone_count} milestones</span>
                    {c.nps_score !== null && (
                      <span className={c.nps_score >= 9 ? 'text-green-400' : c.nps_score >= 7 ? 'text-yellow-400' : 'text-red-400'}>
                        NPS: {c.nps_score}
                      </span>
                    )}
                    {c.latest_milestone && (
                      <span>
                        Latest: {MILESTONE_LABELS[c.latest_milestone as MilestoneType]?.emoji}{' '}
                        {MILESTONE_LABELS[c.latest_milestone as MilestoneType]?.label}
                      </span>
                    )}
                  </div>

                  {c.user_email && (
                    <div className="text-xs text-gray-500">✉️ {c.user_email}</div>
                  )}

                  {/* Status selector */}
                  <div className="flex gap-1 flex-wrap">
                    {STATUS_OPTIONS.map((s) => (
                      <button
                        key={s}
                        onClick={() => updateCandidateStatus(c.user_id, s)}
                        className={`text-xs px-2 py-1 rounded transition ${
                          c.status === s
                            ? STATUS_COLORS[s]
                            : 'bg-white/5 text-gray-500 hover:bg-white/10'
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>

                  {/* Notes */}
                  {selectedCandidate === c.user_id ? (
                    <div className="space-y-2">
                      <textarea
                        className="w-full rounded-md border border-white/20 bg-white/5 px-2 py-1 text-xs text-gray-200 resize-none focus:outline-none focus:ring-1 focus:ring-blue-500"
                        rows={2}
                        value={editingNotes}
                        onChange={(e) => setEditingNotes(e.target.value)}
                        placeholder="Admin notes..."
                      />
                      <div className="flex gap-2">
                        <button onClick={() => updateCandidateNotes(c.user_id)} className="text-xs px-2 py-1 bg-blue-500/20 text-blue-300 rounded">Save</button>
                        <button onClick={() => setSelectedCandidate(null)} className="text-xs px-2 py-1 bg-white/5 text-gray-400 rounded">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => { setSelectedCandidate(c.user_id); setEditingNotes(c.admin_notes || ''); }}
                      className="text-xs text-gray-500 hover:text-gray-300"
                    >
                      {c.admin_notes ? `📝 ${c.admin_notes}` : '+ Add notes'}
                    </button>
                  )}
                </div>
              ))
            )}
          </>
        )}

        {/* Milestones Tab */}
        {!loading && tab === 'milestones' && (
          <>
            {milestones.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <p className="text-4xl mb-3">🎯</p>
                <p className="text-sm">No milestones recorded yet</p>
              </div>
            ) : (
              milestones.map((m) => {
                const config = MILESTONE_LABELS[m.milestone_type as MilestoneType];
                return (
                  <div key={m.id} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{config?.emoji || '📌'}</span>
                      <div>
                        <div className="text-sm font-medium">{config?.label || m.milestone_type}</div>
                        <div className="text-xs text-gray-500">
                          {m.user_type === 'contractor' ? '🏗️' : '👷'} {m.user_id.slice(0, 8)}...
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-gray-400">{timeAgo(m.created_at)}</div>
                      <div className="text-xs text-yellow-500">+{config?.points || 0}pts</div>
                    </div>
                  </div>
                );
              })
            )}
          </>
        )}

        {/* Surveys Tab */}
        {!loading && tab === 'surveys' && (
          <>
            {surveys.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <p className="text-4xl mb-3">📋</p>
                <p className="text-sm">No survey responses yet</p>
                <p className="text-xs mt-1">Users see the survey on their first dashboard visit</p>
              </div>
            ) : (
              surveys.map((s) => (
                <div key={s.id} className="bg-white/5 border border-white/10 rounded-xl p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-sm">
                        {s.survey_type === 'onboarding' ? '🎉' : s.survey_type === 'post_hire' ? '🤝' : '👋'}
                      </span>
                      <span className="font-medium text-sm capitalize">{s.survey_type.replace('_', ' ')}</span>
                      <span className="text-xs text-gray-500">{s.user_type === 'contractor' ? '🏗️' : '👷'}</span>
                    </div>
                    <div className="text-xs text-gray-400">{timeAgo(s.created_at)}</div>
                  </div>

                  {s.nps_score !== null && (
                    <div className={`text-sm font-medium ${s.nps_score >= 9 ? 'text-green-400' : s.nps_score >= 7 ? 'text-yellow-400' : 'text-red-400'}`}>
                      NPS: {s.nps_score}/10
                    </div>
                  )}

                  <div className="space-y-1">
                    {Object.entries(s.responses).map(([key, value]) => (
                      <div key={key} className="text-xs">
                        <span className="text-gray-500">{key.replace(/_/g, ' ')}:</span>{' '}
                        <span className="text-gray-300">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </>
        )}
      </div>
    </div>
  );
}
